import { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

interface QueryResult<T> {
  data: T[] | null;
  error: unknown;
}

interface SingleQueryResult<T> {
  data: T | null;
  error: unknown;
}

interface QueryBuilder<T> extends PromiseLike<QueryResult<T>> {
  select(columns: string): QueryBuilder<T>;
  eq(column: string, value: unknown): QueryBuilder<T>;
  order(column: string, options?: Record<string, unknown>): QueryBuilder<T>;
  update(values: unknown): QueryBuilder<T>;
  single(): Promise<SingleQueryResult<T>>;
}

type UntypedSupabase = {
  from: <T>(table: string) => QueryBuilder<T>;
  rpc: <T = unknown>(fn: string, args: Record<string, unknown>) => Promise<{ data: T | null; error: unknown }>;
};

const db = supabase as unknown as UntypedSupabase;

export type ComplianceFramework = "SOC2" | "ISO-27001" | "HIPAA";
export type ProgramPhase = "policies" | "controls" | "evidence" | "team" | "audit_ready";
export type ProgramStatus = "scoping" | "active" | "paused" | "audit_ready" | "completed" | "archived";
export type ControlStatus = "met" | "partial" | "gap" | "not_applicable";
export type EvidenceStatus = "missing" | "uploaded" | "linked" | "expired" | "accepted";

export interface ComplianceProgram {
  id: string;
  company_id: string;
  framework: ComplianceFramework;
  name: string;
  scope: Record<string, unknown>;
  target_audit_date: string | null;
  phase: ProgramPhase;
  status: ProgramStatus;
  owner_id: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface FrameworkControl {
  id: string;
  framework: ComplianceFramework;
  control_id: string;
  domain: string;
  title: string;
  description: string;
  required_policy_tags: string[];
  required_evidence_types: string[];
  mapped_control_ids: string[];
}

export interface ProgramControl {
  id: string;
  company_id: string;
  program_id: string;
  control_id: string;
  status: ControlStatus;
  owner_id: string | null;
  notes: string | null;
  framework_control?: FrameworkControl | null;
}

export interface ProgramEvidenceItem {
  id: string;
  company_id: string;
  program_id: string;
  control_id: string;
  title: string;
  description: string | null;
  evidence_type: string;
  owner_id: string | null;
  due_date: string | null;
  status: EvidenceStatus;
  knowledge_document_id: string | null;
  recurrence: "monthly" | "quarterly" | "annually" | null;
}

export interface ProgramPolicy {
  id: string;
  title: string;
  status: string;
  version: number;
  description: string | null;
  customization_status: "not_started" | "in_progress" | "complete";
}

export interface ProgramTask {
  id: string;
  title: string;
  status: "not_started" | "in_progress" | "at_risk" | "blocked" | "complete";
  priority: "p0" | "p1" | "p2" | "p3";
  due_date: string | null;
  program_phase: ProgramPhase | null;
}

export interface ProgramEvidenceRow extends ProgramEvidenceItem {
  program?: Pick<ComplianceProgram, "id" | "name" | "framework" | "status"> | null;
  framework_control?: FrameworkControl | null;
  owner?: { id: string; name: string; email?: string | null } | null;
  knowledge_document?: { id: string; name: string } | null;
}

export interface ProgramHealth {
  program: ComplianceProgram;
  totalControls: number;
  metControls: number;
  openTasks: number;
  totalTasks: number;
  satisfiedEvidence: number;
  totalEvidence: number;
  readinessPercent: number;
}

function calculateHealth(
  program: ComplianceProgram,
  controls: ProgramControl[] = [],
  tasks: ProgramTask[] = [],
  evidence: ProgramEvidenceItem[] = []
): ProgramHealth {
  const totalControls = controls.length;
  const metControls = controls.filter((control) => control.status === "met").length;
  const totalTasks = tasks.length;
  const openTasks = tasks.filter((task) => task.status !== "complete").length;
  const totalEvidence = evidence.length;
  const satisfiedEvidence = evidence.filter((item) =>
    ["uploaded", "linked", "accepted"].includes(item.status)
  ).length;

  const controlScore = totalControls ? metControls / totalControls : 0;
  const evidenceScore = totalEvidence ? satisfiedEvidence / totalEvidence : 0;
  const taskScore = totalTasks ? (totalTasks - openTasks) / totalTasks : 0;

  return {
    program,
    totalControls,
    metControls,
    openTasks,
    totalTasks,
    satisfiedEvidence,
    totalEvidence,
    readinessPercent: Math.round((controlScore * 0.55 + evidenceScore * 0.3 + taskScore * 0.15) * 100),
  };
}

export function formatFramework(framework: ComplianceFramework | string) {
  if (framework === "SOC2") return "SOC 2";
  if (framework === "ISO-27001") return "ISO 27001";
  return framework;
}

export function useCompliancePrograms() {
  const { profile } = useAuth();
  const [programs, setPrograms] = useState<ComplianceProgram[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPrograms = useCallback(async () => {
    if (!profile?.company_id) return;

    setLoading(true);
    setError(null);
    try {
      const { data, error: queryError } = await db
        .from("programs")
        .select("*")
        .eq("company_id", profile.company_id)
        .order("created_at", { ascending: false });

      if (queryError) throw queryError;
      setPrograms((data ?? []) as ComplianceProgram[]);
    } catch (err) {
      console.error("Error fetching compliance programs:", err);
      setError(err instanceof Error ? err.message : "Failed to load programs");
    } finally {
      setLoading(false);
    }
  }, [profile?.company_id]);

  useEffect(() => {
    if (!profile?.company_id) return;
    fetchPrograms();
  }, [fetchPrograms, profile?.company_id]);

  const startProgram = async (input: {
    framework: ComplianceFramework;
    scope: Record<string, unknown>;
    targetAuditDate: string | null;
  }) => {
    const { data, error: rpcError } = await db.rpc("start_compliance_program", {
      p_framework: input.framework,
      p_scope: input.scope,
      p_target_audit_date: input.targetAuditDate,
      p_owner_id: profile?.id ?? null,
    });

    if (rpcError) throw rpcError;
    await fetchPrograms();
    return data as string;
  };

  return { programs, loading, error, refetch: fetchPrograms, startProgram };
}

export function useProgramDetails(programId: string | undefined) {
  const { profile } = useAuth();
  const [program, setProgram] = useState<ComplianceProgram | null>(null);
  const [controls, setControls] = useState<ProgramControl[]>([]);
  const [policies, setPolicies] = useState<ProgramPolicy[]>([]);
  const [evidence, setEvidence] = useState<ProgramEvidenceItem[]>([]);
  const [tasks, setTasks] = useState<ProgramTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchDetails = useCallback(async () => {
    if (!programId || !profile?.company_id) return;

    setLoading(true);
    setError(null);
    try {
      const [
        { data: programData, error: programError },
        { data: controlsData, error: controlsError },
        { data: policiesData, error: policiesError },
        { data: evidenceData, error: evidenceError },
        { data: tasksData, error: tasksError },
      ] = await Promise.all([
        db.from("programs").select("*").eq("id", programId).eq("company_id", profile.company_id).single(),
        db
          .from("program_controls")
          .select("*, framework_control:framework_controls(*)")
          .eq("program_id", programId)
          .eq("company_id", profile.company_id),
        db
          .from("policies")
          .select("id,title,status,version,description,customization_status")
          .eq("program_id", programId)
          .eq("company_id", profile.company_id)
          .order("created_at", { ascending: true }),
        db
          .from("program_evidence_items")
          .select("*")
          .eq("program_id", programId)
          .eq("company_id", profile.company_id)
          .order("due_date", { ascending: true, nullsFirst: false }),
        db
          .from("compliance_tasks")
          .select("id,title,status,priority,due_date,program_phase")
          .eq("program_id", programId)
          .eq("company_id", profile.company_id)
          .order("due_date", { ascending: true, nullsFirst: false }),
      ]);

      const firstError = programError || controlsError || policiesError || evidenceError || tasksError;
      if (firstError) throw firstError;

      setProgram(programData as ComplianceProgram);
      setControls((controlsData ?? []) as ProgramControl[]);
      setPolicies((policiesData ?? []) as ProgramPolicy[]);
      setEvidence((evidenceData ?? []) as ProgramEvidenceItem[]);
      setTasks((tasksData ?? []) as ProgramTask[]);
    } catch (err) {
      console.error("Error fetching program details:", err);
      setError(err instanceof Error ? err.message : "Failed to load program");
    } finally {
      setLoading(false);
    }
  }, [programId, profile?.company_id]);

  useEffect(() => {
    if (!programId || !profile?.company_id) return;
    fetchDetails();
  }, [fetchDetails, programId, profile?.company_id]);

  const health = useMemo(
    () => (program ? calculateHealth(program, controls, tasks, evidence) : null),
    [program, controls, tasks, evidence]
  );

  return { program, controls, policies, evidence, tasks, health, loading, error, refetch: fetchDetails };
}

export function useProgramEvidence(programId?: string) {
  const { profile } = useAuth();
  const [evidence, setEvidence] = useState<ProgramEvidenceRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchEvidence = useCallback(async () => {
    if (!profile?.company_id) return;

    setLoading(true);
    setError(null);
    try {
      let query = db
        .from("program_evidence_items")
        .select(`
          *,
          program:programs(id,name,framework,status),
          framework_control:framework_controls(*),
          owner:profiles!program_evidence_items_owner_id_fkey(id,name,email),
          knowledge_document:knowledge_base_documents(id,name)
        `)
        .eq("company_id", profile.company_id)
        .order("due_date", { ascending: true, nullsFirst: false });

      if (programId && programId !== "all") {
        query = query.eq("program_id", programId);
      }

      const { data, error: queryError } = await query;
      if (queryError) throw queryError;
      setEvidence((data ?? []) as ProgramEvidenceRow[]);
    } catch (err) {
      console.error("Error fetching program evidence:", err);
      setError(err instanceof Error ? err.message : "Failed to load evidence");
    } finally {
      setLoading(false);
    }
  }, [profile?.company_id, programId]);

  useEffect(() => {
    if (!profile?.company_id) return;
    fetchEvidence();
  }, [fetchEvidence, profile?.company_id]);

  const updateEvidenceItem = async (itemId: string, updates: Partial<Pick<ProgramEvidenceItem, "status" | "owner_id" | "due_date" | "knowledge_document_id">>) => {
    const { error: updateError } = await db
      .from("program_evidence_items")
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq("id", itemId)
      .eq("company_id", profile?.company_id);

    if (updateError) throw updateError;
    await fetchEvidence();
  };

  return { evidence, loading, error, refetch: fetchEvidence, updateEvidenceItem };
}

export function useActiveProgramHealth() {
  const { programs, loading, error } = useCompliancePrograms();
  const activeProgram = programs.find((program) => ["active", "audit_ready"].includes(program.status));
  const details = useProgramDetails(activeProgram?.id);

  return {
    program: activeProgram ?? null,
    health: details.health,
    loading: loading || (!!activeProgram && details.loading),
    error: error || details.error,
  };
}
